/**
 * BoardShareSheetView — pure presentation for Share board (restyled SE-6554).
 *
 * A CENTERED MODAL. It was built on `MobileSheetChrome`, the shared mobile
 * bottom-sheet chrome, which on a desktop viewport rendered as a full-width
 * band across the bottom of the screen carrying two short sections and a great
 * deal of empty space, with the chrome's top-LEFT ✕ chip. Managing who can see
 * a pipeline is a focused task, so it now sits on the same footing as the Rules
 * panel and the Stage editor — the same drawer/sheet → modal trade
 * `RulesPanelView` made first and `StageEditorView` followed (SE-6554).
 *
 * ### Small screens
 *
 * There is no bottom-sheet variant left: the Modal atom is width-percentage
 * based, so it adapts on a phone, but the swipe-down-to-dismiss affordance goes
 * away with the sheet. That is the same behaviour change `StageEditorView`
 * shipped and Raniel accepted; dismissal is still ✕, backdrop tap, Escape and
 * Android back.
 *
 * ### Why the console was noisy here
 *
 * `MobileSheetChrome` wraps its body in a `PanGestureHandler` (the swipe-down
 * path). react-native-gesture-handler implements that by cloning its single
 * child with `onGestureHandlerEvent` / `onGestureHandlerStateChange`, and the
 * child is a `Stack` — a gluestack VStack, which on web is a raw `<div>` that
 * spreads every prop it receives onto the DOM node. React then rejected both as
 * "Unknown event handler property". Dropping the sheet drops the gesture
 * handler, so the leak goes with it; no other control on this surface passes an
 * `on*` prop to a DOM element (the selects are RN primitives, which
 * react-native-web filters).
 * @remarks View component — no business logic, hooks, or state.
 */

import { ChevronDown, Crown, Plus, XIcon } from "lucide-react-native";
import { memo } from "react";

import BoardShareActionItem from "@/features/player-kanban/components/molecules/BoardShareActionItem";
import {
  ASSIGNABLE_BOARD_ROLES,
  BOARD_ROLE_LABELS,
} from "@/features/player-kanban/constants/assignableBoardRoles";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Modal, ModalBackdrop, ModalContent } from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import SelectDropdown, {
  type SelectDropdownOption,
} from "@/components/molecules/SelectDropdown";

import type { UserKanbanBoardRole } from "@/generated/graphql";

/**
 * Display data for a current board user
 */
interface CurrentUserDisplayData {
  id: string;
  userId: string;
  role: UserKanbanBoardRole;
  userEmail: string;
  userName: string;
}

/**
 * Display data for an available organization user
 */
interface AvailableUserDisplayData {
  userId: string;
  displayName: string;
}

/**
 * Props for BoardShareSheetView
 */
interface BoardShareSheetViewProps {
  isOpen: boolean;
  onClose: () => void;
  currentUsers: CurrentUserDisplayData[];
  availableUsers: AvailableUserDisplayData[];
  currentUserId: string;
  selectedUserId: string;
  selectedRole: UserKanbanBoardRole;
  selectedUserDisplayName: string;
  selectedRoleDisplayText: string;
  loading: boolean;
  isAddingUser: boolean;
  isRemovingUser: boolean;
  onSelectUser: (userId: string) => void;
  onSelectRole: (role: UserKanbanBoardRole) => void;
  onAddUser: () => void;
  onRemoveUser: (userId: string) => void;
  onRoleChange: (userId: string, newRole: UserKanbanBoardRole) => void;
  isUserPopoverOpen: boolean;
  onUserPopoverOpen: () => void;
  onUserPopoverClose: () => void;
  isRolePopoverOpen: boolean;
  onRolePopoverOpen: () => void;
  onRolePopoverClose: () => void;
  userSearchText: string;
  onUserSearchChange: (text: string) => void;

  /**
   * SE-8296 — junction-OWNER authority: add, change role, remove.
   *
   * Separate from `canTransferOwnership` because the server separates them.
   * `addUserToKanbanBoard` and `updateUserKanbanBoardRole` admit the board's
   * OWNER row and nobody else; transfer also admits org MANAGER/ADMIN/OWNER.
   */
  canManageMembers: boolean;
  /** Junction OWNER or org MANAGER/ADMIN/OWNER. */
  canTransferOwnership: boolean;
  /** The incumbent owner's display name, or null for an owner-less board. */
  currentOwnerName: string | null;
  /**
   * True when no OWNER row was seen but the members connection has another
   * page. The owner sorts LAST under `role DESC`, so it is the first row a page
   * limit cuts — absence on one page is not absence.
   */
  isOwnershipUnresolved: boolean;
  /** Active org members eligible to receive ownership. */
  transferCandidates: AvailableUserDisplayData[];
  selectedNewOwnerId: string;
  selectedNewOwnerDisplayName: string;
  isTransferPopoverOpen: boolean;
  onTransferPopoverOpen: () => void;
  onTransferPopoverClose: () => void;
  transferSearchText: string;
  onTransferSearchChange: (text: string) => void;
  onSelectNewOwner: (userId: string) => void;
  onTransferPress: () => void;
  isTransferConfirmOpen: boolean;
  onTransferConfirm: () => void;
  onTransferCancel: () => void;
  transferConfirmBody: string[];
  transferErrorMessage?: string;
  isTransferring: boolean;
}

/**
 * Shared chrome for both selects, so the user picker and the role picker are
 * one control in two widths rather than two controls that resemble each other.
 * The controls family: 36px tall, `outline-strong` hairline, 4px radius.
 */
const SELECT_TRIGGER_CLASS =
  "h-9 w-full justify-center rounded-sm border border-outline-strong bg-surface-base px-3";

/**
 * Section label: the smallest type rung, uppercased and letterspaced, in the
 * muted tier — a divider that happens to have words, not a heading competing
 * with the modal title.
 */
const SECTION_LABEL_CLASS =
  "font-heading uppercase tracking-wide text-content-muted";

/**
 * Pick the primary text class when a value is set, otherwise fall back to the muted class.
 * @param hasValue - Whether a value is currently selected
 * @returns The primary text color class when selected, otherwise the muted class
 * @remarks Extracted so the parent View stays under the cognitive complexity
 * threshold (sonarjs/cognitive-complexity).
 */
const selectedOrMutedClass = (hasValue: unknown): string =>
  hasValue ? "text-content-primary" : "text-content-subtle";

/**
 * Pick the toggle handler for an open/closed popover.
 * @param isOpen - Whether the popover is currently open
 * @param onOpen - Handler to open the popover
 * @param onClose - Handler to close the popover
 * @returns The handler that should fire on the next press
 * @remarks Returns one of the two handlers it was given rather than a fresh
 * closure, so the memoized dropdown keeps a stable `onToggle` identity.
 */
const popoverToggleHandler = (
  isOpen: boolean,
  onOpen: () => void,
  onClose: () => void
): (() => void) => (isOpen ? onClose : onOpen);

/**
 * Builds the option rows for the user picker.
 *
 * The list arrives already filtered by the container's search text — the
 * dropdown renders the search field, the container owns what matches.
 * @param availableUsers - Organization users eligible to be added
 * @param selectedUserId - Currently chosen user, if any
 * @param onSelectUser - Selection handler
 * @returns The user options in SelectDropdown shape
 */
const buildUserOptions = (
  availableUsers: AvailableUserDisplayData[],
  selectedUserId: string,
  onSelectUser: (userId: string) => void
): SelectDropdownOption[] =>
  availableUsers.map(user => ({
    id: user.userId,
    label: user.displayName,
    selected: selectedUserId === user.userId,
    onPress: () => onSelectUser(user.userId),
  }));

/**
 * Builds the role options for the add-user row.
 *
 * SE-8296: driven by `ASSIGNABLE_BOARD_ROLES`. Owner used to sit at the top of
 * this list, and `addUserToKanbanBoard` rejects it with "There can only be one
 * owner per board", so the option could only ever produce an error toast.
 * Ownership moves through the Board owner section instead.
 * @param selectedRole - Role currently chosen for the pending add
 * @param onSelectRole - Selection handler
 * @returns The role options in SelectDropdown shape
 */
export const buildRoleOptions = (
  selectedRole: UserKanbanBoardRole,
  onSelectRole: (role: UserKanbanBoardRole) => void
): SelectDropdownOption[] =>
  ASSIGNABLE_BOARD_ROLES.map(role => ({
    id: role.toLowerCase(),
    label: BOARD_ROLE_LABELS[role],
    selected: selectedRole === role,
    onPress: () => onSelectRole(role),
  }));

/**
 * Renders the Board owner section — SE-8296.
 *
 * The candidate list is the ACTIVE organization roster, not the board member
 * list. Two reasons, both server-shaped: the mutation refuses a target with no
 * ACTIVE organization membership row, and the board member connection does not
 * expose membership status, so a member who has since been deactivated would be
 * offered here and then refused. Sourcing from the org roster also admits a
 * non-member, which is correct — the server creates their OWNER row.
 * @param props - The view props
 * @returns The board-owner section
 */
const renderBoardOwner = (props: BoardShareSheetViewProps) => (
  <Stack className="gap-2">
    <Text variant="micro" className={SECTION_LABEL_CLASS}>
      Board owner
    </Text>

    <Stack
      direction="horizontal"
      className="items-center gap-2 rounded-md border border-outline-subtle px-4 py-3"
    >
      <Icon as={Crown} size="xs" className="text-content-muted" />
      <Text
        variant="body-strong"
        numberOfLines={1}
        className="shrink text-content-primary"
        testID="board-owner-name"
      >
        {props.isOwnershipUnresolved
          ? "Owner not loaded"
          : (props.currentOwnerName ?? "No owner assigned")}
      </Text>
    </Stack>

    <Text variant="caption" className="text-content-subtle">
      {props.isOwnershipUnresolved
        ? "This board has more members than were loaded, and the owner row is the last one returned, so the current owner could not be determined. Transfer is unavailable until it is."
        : props.currentOwnerName
          ? "Transferring makes the chosen member the owner and leaves the current owner as an editor."
          : "This board has no owner, so it cannot be renamed, shared or deleted. Assigning one repairs it."}
    </Text>

    <Stack direction="horizontal" className="items-center gap-2">
      <Box className="flex-1">
        <SelectDropdown
          isOpen={props.isTransferPopoverOpen}
          onToggle={popoverToggleHandler(
            props.isTransferPopoverOpen,
            props.onTransferPopoverOpen,
            props.onTransferPopoverClose
          )}
          onClose={props.onTransferPopoverClose}
          triggerAccessibilityLabel="Select a new owner"
          triggerClassName={SELECT_TRIGGER_CLASS}
          triggerContent={
            <Stack
              direction="horizontal"
              className="w-full items-center justify-between gap-2"
            >
              <Text
                variant="caption"
                numberOfLines={1}
                className={`shrink ${selectedOrMutedClass(props.selectedNewOwnerId)}`}
              >
                {props.selectedNewOwnerDisplayName}
              </Text>
              <Icon as={ChevronDown} size="xs" className="text-content-muted" />
            </Stack>
          }
          options={buildUserOptions(
            props.transferCandidates,
            props.selectedNewOwnerId,
            props.onSelectNewOwner
          )}
          searchValue={props.transferSearchText}
          onSearchChange={props.onTransferSearchChange}
          searchPlaceholder="Search members"
          emptyText="No members found"
        />
      </Box>

      <Button
        label="Transfer"
        variant="solid"
        tone="accent"
        size="sm"
        labelTone="on-accent"
        labelVariant="caption"
        className="h-9 shrink-0 rounded-sm"
        isLoading={props.isTransferring}
        onPress={props.onTransferPress}
        isDisabled={
          props.isTransferring ||
          !props.selectedNewOwnerId ||
          props.isOwnershipUnresolved
        }
        accessibilityLabel="Transfer board ownership"
        testID="transfer-ownership-btn"
      />
    </Stack>
  </Stack>
);

/**
 * Renders the two board-sharing sections. Split out of the modal shell so the
 * shell stays flat and the loading fork has one place to return from.
 * @param props - The view props
 * @returns The Current access + Add user body
 */
const renderBody = (props: BoardShareSheetViewProps) => {
  if (props.loading) {
    return (
      <Box className="items-center justify-center py-10">
        <Spinner size="large" />
      </Box>
    );
  }

  return (
    <Stack className="gap-5 px-5 pb-5 pt-4">
      <Stack className="gap-2">
        <Text variant="micro" className={SECTION_LABEL_CLASS}>
          Current access
        </Text>
        <Box className="overflow-hidden rounded-md border border-outline-subtle">
          <ScrollView
            className="max-h-48"
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
          >
            {props.currentUsers.map(user => (
              <BoardShareActionItem
                key={user.id}
                userKanbanBoardId={user.id}
                userEmail={user.userEmail}
                userName={user.userName}
                currentRole={user.role}
                isCurrentUser={user.userId === props.currentUserId}
                canManageMembers={props.canManageMembers}
                onRoleChange={props.onRoleChange}
                onRemove={props.onRemoveUser}
              />
            ))}
          </ScrollView>
        </Box>
      </Stack>

      {props.canManageMembers && (
        <Stack className="gap-2">
          <Text variant="micro" className={SECTION_LABEL_CLASS}>
            Add user
          </Text>
          <Stack className="gap-2">
            {/* The picker used to expand an inline panel that pushed the role
              row and the button down the sheet as you opened it. It is a real
              anchored dropdown now — the same SelectDropdown the access rows
              above already use — with its search field inside the panel rather
              than as a second field on the surface. The search is not
              decorative: the org list pages 500 members (SE-5378), and
              scrolling that is not a way to find anyone. */}
            <SelectDropdown
              isOpen={props.isUserPopoverOpen}
              onToggle={popoverToggleHandler(
                props.isUserPopoverOpen,
                props.onUserPopoverOpen,
                props.onUserPopoverClose
              )}
              onClose={props.onUserPopoverClose}
              triggerAccessibilityLabel="Select a user to add"
              triggerClassName={SELECT_TRIGGER_CLASS}
              triggerContent={
                <Stack
                  direction="horizontal"
                  className="w-full items-center justify-between gap-2"
                >
                  <Text
                    variant="caption"
                    numberOfLines={1}
                    className={`shrink ${selectedOrMutedClass(props.selectedUserId)}`}
                  >
                    {props.selectedUserDisplayName}
                  </Text>
                  <Icon
                    as={ChevronDown}
                    size="xs"
                    className="text-content-muted"
                  />
                </Stack>
              }
              options={buildUserOptions(
                props.availableUsers,
                props.selectedUserId,
                props.onSelectUser
              )}
              searchValue={props.userSearchText}
              onSearchChange={props.onUserSearchChange}
              searchPlaceholder="Search users"
              emptyText="No users found"
            />

            <Stack direction="horizontal" className="items-center gap-2">
              <Box className="flex-1">
                <SelectDropdown
                  isOpen={props.isRolePopoverOpen}
                  onToggle={popoverToggleHandler(
                    props.isRolePopoverOpen,
                    props.onRolePopoverOpen,
                    props.onRolePopoverClose
                  )}
                  onClose={props.onRolePopoverClose}
                  triggerAccessibilityLabel="Select a role"
                  triggerClassName={SELECT_TRIGGER_CLASS}
                  triggerContent={
                    <Stack
                      direction="horizontal"
                      className="w-full items-center justify-between gap-2"
                    >
                      <Text
                        variant="caption"
                        numberOfLines={1}
                        className={`shrink ${selectedOrMutedClass(props.selectedRole)}`}
                      >
                        {props.selectedRoleDisplayText}
                      </Text>
                      <Icon
                        as={ChevronDown}
                        size="xs"
                        className="text-content-muted"
                      />
                    </Stack>
                  }
                  options={buildRoleOptions(
                    props.selectedRole,
                    props.onSelectRole
                  )}
                />
              </Box>

              {/* A solid accent CTA. It was painted from the Kanban palette —
                `colors.primary` when both selects were filled, `colors.secondary`
                otherwise — and `secondary` is a flat grey with a grey label on
                it, so the disabled button read as broken chrome rather than as a
                button waiting for input. `tone="accent"` gives the resting blue
                and its own hover/pressed shades, and the atom dims the whole
                button while keeping the label on-accent, so it stays legible
                disabled. Never solid `primary`: that is white in dark mode. */}
              <Button
                label="Add User"
                variant="solid"
                tone="accent"
                size="sm"
                icon={Plus}
                iconSize="xs"
                iconTone="on-accent"
                labelTone="on-accent"
                labelVariant="caption"
                className="h-9 shrink-0 rounded-sm"
                isLoading={props.isAddingUser}
                onPress={props.onAddUser}
                isDisabled={
                  props.isAddingUser ||
                  !props.selectedUserId ||
                  !props.selectedRole
                }
                accessibilityLabel="Add user to board"
              />
            </Stack>
          </Stack>
        </Stack>
      )}

      {props.canTransferOwnership && renderBoardOwner(props)}
    </Stack>
  );
};

/**
 * View component for BoardShareSheet — renders the Share board modal.
 * @param props - The component props
 * @returns The Share board modal
 */
const BoardShareSheetView = (props: BoardShareSheetViewProps) => (
  <Modal
    isOpen={props.isOpen}
    onClose={props.onClose}
    closeOnOverlayClick
    size="md"
  >
    <ModalBackdrop />
    <ModalContent testID="board-share-modal" className="!p-0">
      {/* A plain Stack, not `ModalBody` — gluestack's ModalBody IS a ScrollView,
          and the Current access list is a ScrollView of its own, so using it
          would nest two scrollers and let the outer one intercept touches on
          the inner list (the same reason UnifiedTagModalView avoids it). This
          surface has nothing to scroll at the modal level anyway: two short
          sections, and the only list that can outgrow its space caps itself. */}
      <Stack className="w-full">
        <Stack
          direction="horizontal"
          className="items-center justify-between gap-4 border-b border-outline-default px-5 py-4"
        >
          <Text
            variant="title-sm"
            className="min-w-0 flex-1 text-content-primary"
          >
            Share board
          </Text>
          {/* Top-RIGHT, on its own raised chip — the session's modal idiom. The
              sheet chrome put a small circular ✕ at the top LEFT, which is a
              bottom-sheet convention, not a dialog one. */}
          <Pressable
            onPress={props.onClose}
            accessibilityLabel="Close share board"
            accessibilityRole="button"
            testID="board-share-modal-close"
            className="size-8 shrink-0 items-center justify-center rounded-md bg-surface-raised"
          >
            <Icon as={XIcon} size="sm" className="text-content-secondary" />
          </Pressable>
        </Stack>

        {renderBody(props)}
      </Stack>

      <ConfirmDialog
        isOpen={props.isTransferConfirmOpen}
        onClose={props.onTransferCancel}
        onConfirm={props.onTransferConfirm}
        title="Transfer board ownership?"
        body={props.transferConfirmBody}
        icon={Crown}
        tone="warning"
        confirmLabel="Transfer ownership"
        isConfirming={props.isTransferring}
        errorMessage={props.transferErrorMessage}
        size="sm"
        testID="transfer-ownership-confirm"
      />
    </ModalContent>
  </Modal>
);

BoardShareSheetView.displayName = "BoardShareSheetView";

export default memo(BoardShareSheetView);
